import React, { useState } from 'react';
import { resolveUploadUrl } from '../api';
import { 
  Heart, MessageCircle, Flag, ChevronDown, ChevronUp, Trash2, 
  MoreVertical, Repeat2, UserPlus, UserCheck, Mail, Reply, X 
} from 'lucide-react';

// ============================================================
// ✅ SECURITY HELPERS
// ============================================================

/**
 * 1. SANITIZE TEXT INPUT
 * Escapes HTML characters to prevent XSS attacks.
 */
const sanitizeInput = (input) => {
  if (!input) return "";
  return String(input)
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

/**
 * 2. SANITIZE URLS (FIXED)
 * Prevents 'javascript:' injection AND Open Redirects via relative paths.
 * ONLY allows http and https protocols.
 */
const sanitizeUrl = (url) => {
  if (!url) return null;
  
  // Strip whitespace
  const trimmedUrl = url.trim();
  
  // Check for safe protocols explicitly. 
  // FIX: We no longer blindly return relative paths like '/logout'.
  // We only allow absolute URLs starting with http/https.
  if (/^https?:\/\//i.test(trimmedUrl)) {
    return trimmedUrl;
  }
  
  // Block everything else (javascript:, data:, vbscript:, and relative paths)
  return null; 
};

export default function Post({
  post, currentUser, onLike, onAddComment, onReport, onDelete,
  onRepost, onUnrepost, onUserClick, onDeleteComment,
  onFollowUser, onUnfollowUser, isFollowingAuthor = false,
  isProcessingLike = false, isOwnPost = false, renderBadge,
  onMessageUser, highlight = false
}) {
  const [isCommenting, setIsCommenting] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [showAllComments, setShowAllComments] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  
  // --- Data Normalization ---
  const isRepost = post.isRepost || post.is_repost || false;
  const repostedBy = post.repostedBy || post.reposted_by || null;
  const repostRowId = post.repostId || post.repost_id || null;
  const reposterId = post.reposterId || post.reposter_id || null;
  const authorId = post.userId || post.user_id || post.authorId;
  const authorName = post.authorName || post.author_name || 'Anonymous';
  const authorAvatar = post.authorAvatar || post.author_avatar;
  const timestamp = post.created_at || post.timestamp;
  const isAuthorPremium = post.isAuthorPremium || post.author_is_premium || false;
  const content = post.content || '';
  const rawImage = post.image_url || post.image;
  const safeImage = rawImage ? resolveUploadUrl(rawImage) : null;
  const likeCount = post.likes_count ?? post.like_count ?? 0;
  const commentCount = post.comments_count ?? post.comment_count ?? 0;
  const repostCount = post.reposts_count ?? post.repost_count ?? 0;
  const hasLiked = post.has_liked || post.hasLiked || false;
  const hasReposted = post.has_reposted || post.hasReposted || false; 

  // ✅ SECURITY FIX: Normalize URL construction
  // We construct the full URL *before* sanitization so relative paths 
  // become absolute and can be validated against the whitelist.
  const constructSafeUrl = (path) => {
    if (!path) return null;

    // If already absolute, validate directly
    if (path.startsWith('http')) {
      return sanitizeUrl(path);
    }

    // If relative, append to known safe backend base URL
    // This prevents "/logout" or "/admin/delete" from being injected.
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    
    // Ensure no double slashes if path starts with slash
    const fullPath = path.startsWith('/') ? `${baseUrl}${path}` : `${baseUrl}/${path}`;
    
    // Now sanitize the constructed absolute URL
    return sanitizeUrl(fullPath);
  };

  const postImage = safeImage;

  // --- Handlers ---
  const handleCommentSubmit = (e) => {
    e.preventDefault();
    if (commentText.trim()) {
      // ✅ SECURITY: Sanitize comment text before sending to parent/API
      const safeText = sanitizeInput(commentText);
      
      onAddComment(post.id, safeText, replyingTo);
      setCommentText('');
      setReplyingTo(null);
    }
  };

  const handleDeleteClick = () => {
    setShowOptions(false); 
    // If this card is a repost made by the current user, delete the repost row
    // itself (post.id is the ORIGINAL post for repost cards).
    const targetId = (repostRowId && reposterId != null && String(reposterId) === String(currentUser?.id))
      ? repostRowId
      : post.id;
    if (onDelete) onDelete(targetId);
  };

  const handleReportClick = () => {
    setShowOptions(false);
    if (onReport) onReport(post.id);
  };

  const handleMessageClick = () => {
    setShowOptions(false);
    if (onMessageUser) onMessageUser(authorId);
  };

  const handleFollowClick = (e) => {
    e.stopPropagation(); 
    
    const authorData = {
      id: authorId,
      name: authorName,
      avatar: authorAvatar
    };

    if (isFollowingAuthor && onUnfollowUser) {
      onUnfollowUser(authorData); 
    } else if (!isFollowingAuthor && onFollowUser) {
      onFollowUser(authorData); 
    }
  };
  
  const formatTimeAgo = (isoString) => {
    if (!isoString) return 'some time ago';
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return 'some time ago';
    const now = new Date();
    const elapsedSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (elapsedSeconds < 0) return 'just now';
    const intervals = { year: 31536000, month: 2592000, week: 604800, day: 86400, hour: 3600, minute: 60 };
    for (const [unit, seconds] of Object.entries(intervals)) {
      if (elapsedSeconds >= seconds) {
        const interval = Math.floor(elapsedSeconds / seconds);
        return `${interval} ${unit}${interval !== 1 ? 's' : ''} ago`;
      }
    }
    return 'just now';
  };

  const comments = post.comments || [];
  
  const renderAuthorAvatar = () => {
    const safeAvatar = authorAvatar ? resolveUploadUrl(authorAvatar) : null;
    if (safeAvatar) return <img src={safeAvatar} alt={authorName} className="w-full h-full object-cover" />;
    return <span className="text-white font-bold">{authorName?.charAt(0)?.toUpperCase() || 'U'}</span>;
  };

  const renderCommentAvatar = (comment) => {
    const name = comment.author_name || comment.authorName || 'U';
    const avatar = comment.author_avatar || comment.authorAvatar;
    const safeAvatar = avatar ? resolveUploadUrl(avatar) : null;
    if (safeAvatar) return <img src={safeAvatar} alt={name} className="w-full h-full object-cover" />;
    return <span className="text-xs font-bold text-gray-700 dark:text-gray-200">{name.charAt(0)}</span>;
  };

  const visibleComments = showAllComments ? comments : comments.slice(0, 1);

  return (
    <div
      id={`post-${post.id}`}
      className={`bg-white dark:bg-gray-800/60 backdrop-blur-sm p-5 rounded-2xl mb-4 shadow-sm hover:shadow-md transition-all duration-300 relative border border-gray-200 dark:border-gray-700/50 ${highlight ? 'ring-2 ring-blue-500 dark:ring-blue-400 animate-pulse' : ''}`}
    >
      
      {isRepost && repostedBy && (
        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs mb-3 pl-1">
          <Repeat2 size={14} />
          <span>
            <span className="font-semibold hover:underline cursor-pointer" onClick={() => onUserClick && onUserClick(repostedBy.id)}>
              {repostedBy.name}
            </span> Reposted
          </span>
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <button onClick={() => onUserClick && onUserClick(authorId)} className="flex items-center hover:bg-gray-100 dark:hover:bg-gray-700/50 p-1.5 rounded-xl transition-colors text-left flex-grow">
          <div className="w-11 h-11 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold mr-3 flex-shrink-0 overflow-hidden shadow-sm">
            {renderAuthorAvatar()}
          </div>
          <div className="flex-grow min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="font-semibold text-gray-900 dark:text-white truncate">{authorName}</p>
              {renderBadge && renderBadge(isAuthorPremium, "w-4 h-4")}
            </div>
            {post.author_username && (
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">@{post.author_username}</p>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400">{formatTimeAgo(timestamp)}</p>
          </div>
        </button>
        
        <div className="flex items-center gap-2">
          {!isOwnPost && onFollowUser && (
            <button 
              onClick={handleFollowClick}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full transition-all duration-200 ${
                isFollowingAuthor 
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-400 border border-transparent hover:border-red-200 dark:hover:border-red-800' 
                  : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
              }`}
            >
              {isFollowingAuthor ? <><UserCheck size={14} /><span>Following</span></> : <><UserPlus size={14} /><span>Follow</span></>}
            </button>
          )}

          <div className="relative">
            <button onClick={() => setShowOptions(!showOptions)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 z-10">
              <MoreVertical size={18} />
            </button>
            {showOptions && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden">
                {isOwnPost ? (
                  <button onClick={handleDeleteClick} className="w-full text-left px-4 py-3 text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center gap-2 text-sm font-medium">
                    <Trash2 size={16} /> <span>{isRepost && reposterId != null ? 'Delete Repost' : 'Delete Post'}</span>
                  </button>
                ) : (
                  <div className="flex flex-col">
                    {onMessageUser && (
                      <button onClick={handleMessageClick} className="w-full text-left px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center gap-2 text-sm font-medium border-b border-gray-100 dark:border-gray-800">
                        <Mail size={16} /> <span>Message User</span>
                      </button>
                    )}
                    <button onClick={handleReportClick} className="w-full text-left px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center gap-2 text-sm font-medium">
                      <Flag size={16} /> <span>Report Post</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ✅ SECURITY: content is rendered inside {} which React auto-escapes */}
      <p className="text-gray-800 dark:text-gray-100 text-base leading-relaxed mb-3 whitespace-pre-wrap">{content}</p>
      
      {/* ✅ SECURITY: Only render image if URL was successfully sanitized */}
      {postImage && (
        <div className="mb-3 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <img src={postImage} alt="Post content" className="w-full h-auto object-cover max-h-[500px]" loading="lazy" />
        </div>
      )}

      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 border-t border-b border-gray-200 dark:border-gray-700/50 py-2 mt-2">
        <button onClick={() => onLike(post.id)} disabled={isProcessingLike} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${hasLiked ? 'text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/20' : 'hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:text-red-500'} ${isProcessingLike ? 'opacity-50 cursor-not-allowed' : ''}`}>
          <Heart size={18} fill={hasLiked ? 'currentColor' : 'none'} />
          <span className="font-semibold">{likeCount > 0 ? likeCount : ''}</span>
        </button>
        <button onClick={() => setIsCommenting(!isCommenting)} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:text-blue-500 dark:hover:text-blue-400 ${isCommenting ? 'text-blue-500 bg-blue-50 dark:bg-blue-900/20' : ''}`}>
          <MessageCircle size={18} />
          <span className="font-semibold">{(comments.length > 0 || commentCount > 0) ? (commentCount || comments.length) : ''}</span>
        </button>
        <button onClick={() => { if (hasReposted && onUnrepost) onUnrepost(post.id); else if (!hasReposted && onRepost) onRepost(post.id); }} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:text-green-500 dark:hover:text-green-400 ${hasReposted ? 'text-green-500 dark:text-green-400 bg-green-50 dark:bg-green-900/20' : ''}`}>
          <Repeat2 size={18} className={hasReposted ? 'fill-current' : ''} />
          <span className="font-semibold">{repostCount > 0 ? repostCount : ''}</span>
        </button>
      </div>

      <div className="mt-3 space-y-2">
        {comments.length > 1 && (
          <button onClick={() => setShowAllComments(!showAllComments)} className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-xs mt-1 flex items-center gap-1 font-medium">
            {showAllComments ? <><ChevronUp size={14} /> Hide comments</> : <><ChevronDown size={14} /> View all {comments.length} comments</>}
          </button>
        )}
        {visibleComments.map(comment => {
          const commentAuthorId = comment.user_id || comment.userId || comment.authorId;
          const isCommentOwner = String(currentUser?.id) === String(commentAuthorId);
          return (
            <div key={comment.id} className="flex items-start gap-2 group pt-1">
              <div className="w-7 h-7 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center text-gray-500 dark:text-gray-300 flex-shrink-0 overflow-hidden border border-gray-300 dark:border-gray-600">
                {renderCommentAvatar(comment)}
              </div>
              <div className="flex-1 bg-gray-100 dark:bg-gray-700/40 rounded-xl p-2 relative">
                <div className="flex justify-between items-start">
                  <div>
                    <button onClick={() => onUserClick && onUserClick(commentAuthorId)} className="font-semibold text-xs text-gray-900 dark:text-white hover:underline mr-2">
                      {comment.author_name || comment.authorName || "User"}
                    </button>
                    {/* ✅ SECURITY: React escapes content inside {} automatically */}
                    <span className="text-gray-700 dark:text-gray-300 text-xs">{comment.content}</span>
                  </div>
                  {isCommentOwner && onDeleteComment && (
                    <button onClick={() => onDeleteComment(comment.id)} className="text-gray-400 hover:text-red-600 dark:hover:text-red-500 transition-colors ml-2 opacity-0 group-hover:opacity-100" title="Delete comment">
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">
                    {formatTimeAgo(comment.created_at)}
                  </span>
                  <button 
                    onClick={() => {
                      setReplyingTo(comment.id);
                      setIsCommenting(true);
                    }} 
                    className="text-[10px] text-blue-500 dark:text-blue-400 font-semibold hover:underline"
                  >
                    Reply
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        
        {isCommenting && (
          <form onSubmit={handleCommentSubmit} className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700/50">
            {replyingTo && (
              <div className="flex justify-between items-center bg-blue-50 dark:bg-blue-900/20 px-3 py-1.5 rounded-t-lg text-xs text-blue-600 dark:text-blue-400 border border-b-0 border-blue-100 dark:border-blue-900">
                <span>Replying to comment</span>
                <button type="button" onClick={() => setReplyingTo(null)} className="font-bold hover:text-blue-800 dark:hover:text-blue-300">
                  <X size={12} />
                </button>
              </div>
            )}
            <div className="flex gap-2">
              <input 
                type="text" 
                value={commentText} 
                onChange={(e) => setCommentText(e.target.value)} 
                placeholder={replyingTo ? "Write a reply..." : "Write a comment..."} 
                className="flex-grow bg-gray-100 dark:bg-gray-700/50 text-gray-900 dark:text-white p-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500 border border-transparent focus:border-blue-500" 
              />
              <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm">Post</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}